import PmaxMomentReview from './MomentReview/PmaxMomentReview';
/**
 * PerfGridPage  (gated behind the `enable-v3-ui-perf-grid` account setting;
 * the Apply button can be hidden per-account via the
 * `disable-v3-apply-recommendation-button` account setting)
 *
 * A focused "Triggers" page split into two halves:
 *
 *   TOP — Triggers Agent. On load it AUTO-RUNS the agent in DRY-RUN across
 *   ALL of the account's perf-agent-eligible campaigns (empty guidance message),
 *   choosing each campaign's agent by destination (Google Ads / LinkedIn /
 *   Facebook) and batching per customer / ad account. Recommendations stream
 *   back ranked by score and render as editable cards (tagged with their source
 *   campaign). The agent emits one type per campaign channel:
 *     • search_ad_group     (SEARCH)          — new ad group: keywords + CPC
 *     • pmax_search_themes  (PERFORMANCE_MAX) — themes for an asset group
 *     • demandgen_targeting (DEMAND_GEN)      — targeting for an ad group
 *     • linkedin_campaign   (LINKEDIN)        — new campaign in the campaign group
 *     • fb_ad_set           (FACEBOOK)        — new PAUSED ad set in the campaign
 *   The user tweaks them, selects the ones they want, and clicks Apply — which
 *   sends each campaign's edited plan to the agent's apply_dryrun path (creates
 *   a paused ad group / appends search themes / sets ad-group targeting / adds a
 *   draft LinkedIn campaign / adds a paused Facebook ad set).
 *
 *   BOTTOM — a mini version of the Campaign Grid: a flat table of the account's
 *   perf-agent-eligible campaigns with cached metrics, NO hierarchy
 *   expand/collapse. Clicking a row filters the recommendations above to that
 *   campaign.
 *
 * Backed by the same endpoints as CampaignGridPage:
 *   GET   /api/v2/campaign-grid                  campaigns + cached metrics
 *   POST  /api/v2/campaign-grid/:id/run-agent    dry-run (message) / apply (apply_plan)
 *   GET   /api/v2/campaign-grid/runs/:runId       poll a run for its result
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MomentReview from './MomentReview/MomentReview';
import SearchMomentReview from './MomentReview/SearchMomentReview';
import { api } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { getUserData } from '../utils/userUtils';
import { isSuperAdmin } from '../utils/permissions';
import { formatRefreshedAgo, newestMetricsFetchedAt } from '../utils/refreshedLabel';
import {
  latestPullWindow,
} from '../utils/campaignGridTimeFilter';
import './PerfGridPage.css';
import ContextEnginePanel from './ContextEnginePanel';

// ─── Destination → format / agent compatibility ────────────────────────────
const G_ADS_DESTINATIONS = new Set([
  'google-ads-search', 'google_search', 'search',
  'google-ads-pmax', 'google_pmax', 'pmax',
  'google-ads-demandgen', 'google_demandgen', 'demandgen', 'demand_gen',
  'g-ads',
]);
const LINKEDIN_DESTINATIONS = new Set(['linkedin', 'linkedin-ads', 'linkedin_ads']);
const FB_DESTINATIONS = new Set(['facebook', 'fb', 'instagram']);

// A campaign is perf-agent-eligible if it belongs to a platform we have an
// agent for; the agentType is chosen by destination so g-ads, LinkedIn, and
// Facebook campaigns run their respective agents (concurrently, from runAll).
function agentTypeForDestination(dest) {
  if (G_ADS_DESTINATIONS.has(dest)) return 'g-ads-perf';
  if (LINKEDIN_DESTINATIONS.has(dest)) return 'linkedin-perf';
  if (FB_DESTINATIONS.has(dest)) return 'fb-perf';
  return null;
}

function destinationFormat(dest) {
  const d = String(dest || '').toLowerCase();
  if (d === 'google-ads-search' || d === 'google_search' || d === 'search') return 'Search';
  if (d === 'google-ads-pmax' || d === 'google_pmax' || d === 'pmax') return 'PMax';
  if (d === 'google-ads-demandgen' || d === 'google_demandgen' || d === 'demandgen') return 'DemandGen';
  if (d === 'g-ads') return 'Search';
  if (d === 'linkedin' || d === 'linkedin-ads' || d === 'linkedin_ads') return 'LinkedIn';
  if (d === 'facebook' || d === 'fb' || d === 'instagram') return 'Facebook';
  return 'Other';
}

// ─── Small formatting helpers (mirror CampaignGridPage) ─────────────────────
// Money formatter. `code` is the campaign's platform currency (ISO 4217, from
// the Google Ads customer via the grid pull); omitted/unknown falls back to
// the legacy plain-$ rendering so USD output is unchanged.
const cur = (v, code) => {
  if (v == null) return '—';
  if (code && code !== 'USD') {
    try {
      return Number(v).toLocaleString('en-US', {
        style: 'currency', currency: code,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    } catch (e) { /* unknown code — fall through to legacy $ */ }
  }
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Just the currency SYMBOL (e.g. "$", "€", "£") for inline labels/tooltips where
// we prefix a raw number rather than format the whole value. Falls back to "$"
// for USD or an unknown code so existing output is unchanged.
const curSym = (code) => {
  if (!code || code === 'USD') return '$';
  try {
    const part = new Intl.NumberFormat('en-US', { style: 'currency', currency: code })
      .formatToParts(0).find(p => p.type === 'currency');
    return part ? part.value : '$';
  } catch (e) { return '$'; }
};
const num = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  // Never print "NaN" — an unparseable value reads as missing, which it is.
  return Number.isFinite(n) ? Math.round(n).toLocaleString() : '—';
};
// Conversions come off the platform as FLOATS (Google Ads reports e.g. 3.5), so
// they must not be rounded like impressions/clicks. Mirrors CampaignGridPage.
const numFlex = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const MATCH_TYPES = ['BROAD', 'PHRASE', 'EXACT'];

// ─── Recommendation types ───────────────────────────────────────────────────
// The perf agent emits ONE recommendation type per campaign channel:
//   search_ad_group     (SEARCH)          → new ad group w/ keywords + CPC
//   pmax_search_themes  (PERFORMANCE_MAX) → themes appended to an asset group
//   demandgen_targeting (DEMAND_GEN)      → targeting set on an existing ad group
//   linkedin_campaign   (LINKEDIN)        → new campaign in the campaign group
//   fb_ad_set           (FACEBOOK)        → new PAUSED ad set in the campaign
const REC_TYPE_LABEL = {
  search_ad_group: 'Search ad group',
  pmax_search_themes: 'PMax search themes',
  demandgen_targeting: 'DemandGen audience',
  linkedin_campaign: 'LinkedIn campaign',
  fb_ad_set: 'Facebook ad set',
};
const REC_TYPE_TAG = {
  search_ad_group: 'Search',
  pmax_search_themes: 'PMax',
  demandgen_targeting: 'DemandGen',
  linkedin_campaign: 'LinkedIn',
  fb_ad_set: 'Facebook',
};

// DemandGen targeting dimensions — enum options mirror the values the backend
// (DemandGenCampaignCreator._create_ad_group_targeting_operations) accepts.
// `interests` is free text (resolved server-side via GoogleAdsTargetingLookup).
const TARGETING_DIMS = [
  { key: 'genders', label: 'Gender', options: ['MALE', 'FEMALE', 'UNDETERMINED'] },
  { key: 'age_ranges', label: 'Age', options: [
    'AGE_RANGE_18_24', 'AGE_RANGE_25_34', 'AGE_RANGE_35_44', 'AGE_RANGE_45_54',
    'AGE_RANGE_55_64', 'AGE_RANGE_65_UP', 'AGE_RANGE_UNDETERMINED'] },
  { key: 'income_ranges', label: 'Income', options: [
    'INCOME_RANGE_0_50', 'INCOME_RANGE_50_60', 'INCOME_RANGE_60_70', 'INCOME_RANGE_70_80',
    'INCOME_RANGE_80_90', 'INCOME_RANGE_90_UP', 'INCOME_RANGE_UNDETERMINED'] },
  { key: 'parental_statuses', label: 'Parental', options: ['PARENT', 'NOT_A_PARENT', 'UNDETERMINED'] },
];
const ENUM_LABEL = {
  MALE: 'Male', FEMALE: 'Female', UNDETERMINED: 'Unknown', NOT_A_PARENT: 'Not a parent', PARENT: 'Parent',
  AGE_RANGE_18_24: '18–24', AGE_RANGE_25_34: '25–34', AGE_RANGE_35_44: '35–44',
  AGE_RANGE_45_54: '45–54', AGE_RANGE_55_64: '55–64', AGE_RANGE_65_UP: '65+', AGE_RANGE_UNDETERMINED: 'Unknown age',
  INCOME_RANGE_0_50: 'Lower 50%', INCOME_RANGE_50_60: '50–60%', INCOME_RANGE_60_70: '60–70%',
  INCOME_RANGE_70_80: '70–80%', INCOME_RANGE_80_90: '80–90%', INCOME_RANGE_90_UP: 'Top 10%',
  INCOME_RANGE_UNDETERMINED: 'Unknown income',
};
const fmtEnum = (v) => ENUM_LABEL[v] || v;

// ─── Search volume ──────────────────────────────────────────────────────────
// The perf agent enriches every suggested keyword / search theme with Google's
// historical metrics (perf_agent/g_ads_keyword_metrics.py) so a reviewer can
// judge a recommendation's upside before applying it. Metric keys are ABSENT
// (not 0) when Google has no data — a real 0 means "nobody searches this",
// which is itself a reason to drop the keyword, so the two must render
// differently.
const KW_METRIC_KEYS = [
  'avg_monthly_searches', 'competition', 'competition_index',
  'low_top_of_page_bid', 'high_top_of_page_bid', 'average_cpc',
];

/** Copy through whichever search-volume metrics are present on a keyword/theme. */
function pickKwMetrics(src) {
  const out = {};
  for (const k of KW_METRIC_KEYS) {
    if (src?.[k] !== undefined && src?.[k] !== null) out[k] = src[k];
  }
  return out;
}

const hasVolume = (k) => typeof k?.avg_monthly_searches === 'number';

/** 90500 → "90.5K", 1200000 → "1.2M". */
function fmtVolume(n) {
  if (typeof n !== 'number') return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const COMPETITION_LABEL = { LOW: 'Low', MEDIUM: 'Med', HIGH: 'High', UNSPECIFIED: '—', UNKNOWN: '—' };

/** Total monthly demand behind a recommendation, or null when nothing is known.
 *  Search recs aggregate `keywords`; PMax recs aggregate `search_theme_metrics`. */
function recVolume(rec) {
  const entries = (rec?.type === 'pmax_search_themes')
    ? (rec.search_theme_metrics || [])
    : (rec?.keywords || []);
  const known = entries.filter(hasVolume);
  if (known.length === 0) return null;
  return {
    total: known.reduce((s, k) => s + k.avg_monthly_searches, 0),
    withData: known.length,
    total_count: entries.length,
  };
}

/** Tooltip detail for one keyword's metrics. `sym` is the campaign's currency
 *  symbol (defaults to "$" — the Google Keyword Planner bids are reported in the
 *  account's billing currency, not necessarily USD). */
function kwMetricTitle(k, sym = '$') {
  if (!hasVolume(k)) return 'No search-volume data from Google for this keyword';
  const parts = [`${k.avg_monthly_searches.toLocaleString()} avg monthly searches`];
  if (k.competition) parts.push(`Competition: ${COMPETITION_LABEL[k.competition] || k.competition}`);
  if (typeof k.competition_index === 'number') parts.push(`Competition index: ${k.competition_index}/100`);
  if (k.low_top_of_page_bid != null && k.high_top_of_page_bid != null) {
    parts.push(`Top-of-page bid: ${sym}${k.low_top_of_page_bid}–${sym}${k.high_top_of_page_bid}`);
  }
  if (k.average_cpc != null) parts.push(`Avg CPC: ${sym}${k.average_cpc}`);
  return parts.join('\n');
}

/** Volume + competition badges shown beside a keyword / theme input. */
function KwMetrics({ k, sym = '$' }) {
  const known = hasVolume(k);
  const comp = known && k.competition ? (COMPETITION_LABEL[k.competition] || k.competition) : null;
  return (
    <span className="pg-kwm" title={kwMetricTitle(k, sym)}>
      <span className={`pg-kwm-vol${known ? (k.avg_monthly_searches === 0 ? ' is-zero' : '') : ' is-unknown'}`}>
        {known ? `${fmtVolume(k.avg_monthly_searches)}/mo` : 'no data'}
      </span>
      {comp && <span className={`pg-kwm-comp is-${String(k.competition).toLowerCase()}`}>{comp}</span>}
    </span>
  );
}

/** Normalize a targeting object so every dimension is an array we can edit. */
function normTargeting(t) {
  const src = t || {};
  return {
    genders: Array.isArray(src.genders) ? [...src.genders] : [],
    age_ranges: Array.isArray(src.age_ranges) ? [...src.age_ranges] : [],
    income_ranges: Array.isArray(src.income_ranges) ? [...src.income_ranges] : [],
    parental_statuses: Array.isArray(src.parental_statuses) ? [...src.parental_statuses] : [],
    interests: Array.isArray(src.interests)
      ? src.interests.map(i => typeof i === 'string' ? i : (i.keyword || i.name || '')).filter(Boolean)
      : [],
  };
}

/** Strip empty dimensions so we don't send {genders: []} to the agent. */
function compactTargeting(t) {
  const out = {};
  for (const k of ['genders', 'age_ranges', 'income_ranges', 'parental_statuses', 'interests']) {
    if (Array.isArray(t?.[k]) && t[k].length) out[k] = t[k];
  }
  return out;
}

/** Build the apply_plan item the agent's apply_dryrun expects for a rec, or
 *  null if it has no applyable content (no keywords / themes / targeting, or a
 *  missing target id). The shapes mirror g_ads_perf_agent._create_search_adgroup
 *  / _add_pmax_search_themes / _add_demandgen_targeting. */
const requiresMomentReview = rec => rec.type === 'demandgen_targeting' || (['search_ad_group','pmax_search_themes'].includes(rec.type) && rec._source === 'cron');

function buildApplyItem(rec) {
  if (requiresMomentReview(rec)) return null;
  const type = rec.type || 'search_ad_group';
  const base = { type, category: rec.category, score: rec.score, rationale: rec.rationale, name: rec.name };
  if (type === 'search_ad_group') {
    const keywords = (rec.keywords || [])
      .filter(k => k.keyword && k.keyword.trim())
      .map(k => ({ keyword: k.keyword.trim(), match_type: k.match_type || 'BROAD' }));
    if (keywords.length === 0) return null;
    return { ...base, cpc_bid: rec.cpc_bid ?? undefined, keywords };
  }
  if (type === 'pmax_search_themes') {
    const search_themes = (rec.search_themes || []).map(t => String(t).trim()).filter(Boolean);
    if (search_themes.length === 0 || !rec.asset_group_id) return null;
    return { ...base, asset_group_id: rec.asset_group_id, asset_group_name: rec.asset_group_name, search_themes };
  }
  if (type === 'demandgen_targeting') {
    const t = rec.targeting || {};
    const targeting = compactTargeting({
      genders: t.genders || [], age_ranges: t.age_ranges || [], income_ranges: t.income_ranges || [],
      parental_statuses: t.parental_statuses || [],
      interests: (t.interests || []).map(x => String(x).trim()).filter(Boolean),
    });
    // Apply always creates a NEW paused ad group from this targeting — it never
    // modifies an existing ad group, so an ad_group_id is not required (and the
    // generator no longer emits one). The only content requirement is non-empty
    // targeting. Support both shapes: newer recs carry ad_group_name only; older
    // ones may still include ad_group_id, which we pass through for reference
    // (the apply path ignores it).
    if (Object.keys(targeting).length === 0) return null;
    const item = { ...base, ad_group_name: rec.ad_group_name, targeting };
    if (rec.ad_group_id) item.ad_group_id = rec.ad_group_id;
    return item;
  }
  if (type === 'linkedin_campaign') {
    // apply_dryrun rebuilds the DRAFT campaign from the plan item; targetingConfig
    // is required (the agent resolves it to LinkedIn URNs). Delivery overrides
    // (objective / optimization / budget) are optional — omitted => inherit template.
    const tc = rec.targetingConfig || {};
    if (!tc || Object.keys(tc).length === 0) return null;
    const item = { ...base, targetingConfig: tc };
    if (rec.objectiveType) item.objectiveType = rec.objectiveType;
    if (rec.optimizationTargetType) item.optimizationTargetType = rec.optimizationTargetType;
    if (rec.dailyBudget && rec.dailyBudget.amount) item.dailyBudget = rec.dailyBudget;
    return item;
  }
  if (type === 'fb_ad_set') {
    // apply_dryrun creates a new PAUSED ad set: it clones the campaign's template
    // ad set and layers targeting_modifications on top (empty => copy template
    // as-is). optimization_goal is an optional override. A name is the only hard
    // requirement — it's what distinguishes the new ad set from the template.
    if (!rec.name || !String(rec.name).trim()) return null;
    const item = { ...base, targeting_modifications: rec.targeting_modifications || {} };
    if (rec.optimization_goal) item.optimization_goal = rec.optimization_goal;
    return item;
  }
  return null;
}

/** Pull a short, human-readable reason out of an apply error. Google Ads
 *  failures arrive as a large gRPC dump with the useful text on a `message:`
 *  line; surface that, else the first line, capped so the log stays readable. */
function shortenError(err) {
  if (!err) return 'Apply failed';
  const s = String(err);
  const m = s.match(/message:\s*"([^"]+)"/);
  if (m) return m[1];
  return s.split('\n')[0].replace(/\s+/g, ' ').trim().slice(0, 200) || 'Apply failed';
}

/** Human label for what applying a rec of this type did on its platform. */
function applyActionLabel(type) {
  if (type === 'pmax_search_themes') return 'Search themes added';
  if (type === 'demandgen_targeting') return 'Audience targeting applied';
  if (type === 'linkedin_campaign') return 'New campaign created (draft)';
  if (type === 'fb_ad_set') return 'New ad set created (paused)';
  return 'New ad group created (paused)';
}

/** Format an apply-confirmation timestamp (ms) as a short local time. */
function fmtApplyTime(ms) {
  try { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}

const IMPACT_STATUS = {
  measuring: 'Measuring',
  waiting_for_delivery: 'Waiting for delivery',
  insufficient_data: 'Early result',
  measured: 'Measured',
  baseline_unavailable: 'No baseline',
  window_mismatch: 'Window changed',
};

function fmtImpactDate(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

function fmtRate(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : '—';
}

function fmtWindow(window) {
  if (!window) return 'Lifetime';
  if (!window.start_date && !window.end_date) return 'Lifetime';
  return `${window.start_date || '…'} – ${window.end_date || '…'}`;
}

function ImpactRate({ before, after, delta }) {
  const d = Number(delta);
  const hasDelta = Number.isFinite(d);
  const cls = hasDelta ? (d > 0 ? 'gain' : d < 0 ? 'loss' : 'flat') : '';
  return (
    <span className="pg-impact-rate">
      <span>{fmtRate(before)} → {fmtRate(after)}</span>
      <strong className={cls}>{hasDelta ? `${d > 0 ? '+' : ''}${(d * 100).toFixed(2)} pp` : '—'}</strong>
    </span>
  );
}

/** Link text for the applied rec's Google Ads link (opens its campaign). */
function adsLinkLabel() {
  return 'Open campaign in Google Ads ↗';
}

/** Google Ads link for an applied rec — opens the campaign scoped to the
 *  relevant menu (PMax → asset groups, Search/DemandGen → ad groups).
 *
 *  A precise deep link to a specific entity isn't possible: the Google Ads UI
 *  needs an obfuscated account id (ocid) for account context, and that's not
 *  exposed by the API / derivable from the numeric customer id. So we link at
 *  the campaign level and rely on the user's active Google Ads session for the
 *  account — worst case they land on the campaign in whichever account is
 *  currently selected. campaignId comes from platform_campaign_id (first id if
 *  the campaign maps to several). */
function buildAdsLink(rec, campaign) {
  if (!rec?.applied) return null;
  // Google Ads deep-link only. LinkedIn campaign ids are also numeric, so gate
  // on destination to avoid pointing a LinkedIn rec at a bogus Google Ads URL
  // (LinkedIn applied recs simply show no deep link).
  if (!G_ADS_DESTINATIONS.has(campaign?.destination)) return null;
  const campaignId = String(campaign?.platform_campaign_id || '')
    .split(',')[0]
    .replace(/[^0-9]/g, '');
  if (!campaignId) return null;
  const view = (rec.type === 'pmax_search_themes') ? 'assetgroups' : 'adgroups';
  return `https://ads.google.com/aw/${view}?campaignId=${campaignId}`;
}

/** Human-readable message from a finalized error run (result.errors /
 *  result.error / the stringified run.error), for surfacing to the user. */
function describeRunError(run) {
  const errs = run && run.result && run.result.errors;
  if (Array.isArray(errs) && errs.length) return errs.join('; ');
  if (run && run.result && run.result.error) return String(run.result.error);
  if (run && run.error) {
    try { const p = JSON.parse(run.error); return Array.isArray(p) ? p.join('; ') : String(p); }
    catch { return String(run.error); }
  }
  return 'The recommendation run failed';
}

// The "Refreshed X ago" formatter now lives in utils/refreshedLabel.js, shared
// with CampaignGridPage — see formatRefreshedAgo.

/** Campaign display status — consistent with CampaignGridPage's campaign rows.
 *  Prefer the campaign's OWN platform delivery status (ENABLED / PAUSED) from
 *  the grid pull. Only when it's absent do we fall back to the internal
 *  lifecycle status ('ready' → ENABLED). We deliberately do NOT roll up
 *  ad-group statuses: an ENABLED campaign whose older ad groups are paused
 *  would otherwise read as PAUSED. */
function campaignDisplayStatus(c) {
  if (c.platform_status) return c.platform_status;
  const v = String(c.status || '').toLowerCase();
  if (v === 'ready' || v === 'live' || v === 'active') return 'ENABLED';
  return c.status;
}

/** Is this campaign active (ENABLED) — i.e. eligible for the auto-run? */
function isActiveCampaign(c) {
  const s = String(campaignDisplayStatus(c) || '').toLowerCase();
  return s === 'enabled' || s === 'active' || s === 'live';
}

/** Map a campaign's (possibly comma-separated) external IDs to the campaign. */
function collectPlatformIds(c) {
  return String(c.platform_campaign_id || '')
    .split(',').map(s => s.trim()).filter(Boolean);
}

function StatusPill({ status }) {
  if (!status) return <span className="pg-dash">—</span>;
  const s = String(status).toLowerCase();
  const cls = s === 'active' || s === 'live' || s === 'enabled' ? 'pg-pill-green'
    : s === 'paused' || s === 'stopped' ? 'pg-pill-amber'
    : s === 'failed' || s === 'error' ? 'pg-pill-red'
    : s === 'draft' || s === 'archived' ? 'pg-pill-gray'
    : 'pg-pill-blue';
  return <span className={`pg-pill ${cls}`}>{status}</span>;
}

/**
 * Scope picker for "Run Moments" (super admin).
 *
 * A trigger run used to fan out across EVERY grid-eligible campaign in the
 * account, which is both slow (each campaign is planned sequentially, hence the
 * 2h poll cap) and indiscriminate. This modal lets the runner narrow it to a
 * chosen set; "Entire account" stays the default so the button's old behaviour
 * is one click away and identical on the wire (no campaignIds sent).
 *
 * `campaigns` are the page's perf-agent-eligible rows — the same list the
 * bottom grid renders. Server-side eligibility is still authoritative: the
 * endpoint intersects the selection with its own eligible set.
 */
function RunMomentsModal({ campaigns, onCancel, onRun }) {
  const [scope, setScope] = useState('all');       // 'all' | 'selected'
  const [ids, setIds] = useState(() => new Set()); // internal campaign ids
  const [search, setSearch] = useState('');

  // Escape closes — the backdrop click alone leaves keyboard users stuck.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.destination?.toLowerCase().includes(q) ||
      c.folder_name?.toLowerCase().includes(q)
    );
  }, [campaigns, search]);

  // Checking a campaign implies "selected" scope — otherwise a user ticks boxes
  // with the radio still on "Entire account" and runs the whole account anyway.
  const toggle = (id) => {
    setScope('selected');
    setIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Select-all / clear act on what's VISIBLE (the search result), so they mean
  // "all of these" rather than silently touching filtered-out campaigns.
  const selectVisible = () => {
    setScope('selected');
    setIds(prev => { const next = new Set(prev); for (const c of visible) next.add(c.id); return next; });
  };
  const clearVisible = () => {
    setIds(prev => { const next = new Set(prev); for (const c of visible) next.delete(c.id); return next; });
  };

  const canRun = scope === 'all' || ids.size > 0;

  return (
    <div className="pg-modal-backdrop" onClick={onCancel}>
      <div
        className="pg-modal" role="dialog" aria-modal="true" aria-label="Run Moments"
        onClick={e => e.stopPropagation()}
      >
        <div className="pg-modal-head">
          <div className="pg-modal-title">⚡ Run Moments</div>
          <button type="button" className="pg-modal-close" onClick={onCancel} aria-label="Close">×</button>
        </div>
        <div className="pg-modal-sub">
          Evaluate this account's moment triggers now and chain the ones that fire
          into the perf agent. Choose how much of the account to run.
        </div>

        <div className="pg-modal-scope">
          <label className="pg-modal-radio">
            <input
              type="radio" name="pg-moments-scope"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
            />
            <span>Entire account <span className="pg-muted">({campaigns.length} campaign{campaigns.length === 1 ? '' : 's'})</span></span>
          </label>
          <label className="pg-modal-radio">
            <input
              type="radio" name="pg-moments-scope"
              checked={scope === 'selected'}
              onChange={() => setScope('selected')}
            />
            <span>Selected campaigns only <span className="pg-muted">({ids.size} selected)</span></span>
          </label>
        </div>

        <div className={`pg-modal-list-wrap${scope === 'all' ? ' dimmed' : ''}`}>
          <div className="pg-modal-list-head">
            <input
              className="pg-grid-search"
              placeholder="Search campaigns"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="button" className="pg-link" onClick={selectVisible}>Select all</button>
            <button type="button" className="pg-link" onClick={clearVisible}>Clear</button>
          </div>
          <div className="pg-modal-list">
            {visible.length === 0 && <div className="pg-empty">No campaigns match.</div>}
            {visible.map(c => (
              <label key={c.id} className="pg-modal-row">
                <input type="checkbox" checked={ids.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="pg-modal-row-name">{c.name}</span>
                <span className="pg-pill pg-pill-blue">{destinationFormat(c.destination)}</span>
                <span className="pg-muted">{c.folder_name || '—'}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="pg-modal-foot">
          <button type="button" className="pg-btn pg-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            type="button" className="pg-btn pg-btn-ai"
            disabled={!canRun}
            onClick={() => onRun(scope === 'selected' ? Array.from(ids) : null)}
          >
            {scope === 'selected'
              ? `Run on ${ids.size} campaign${ids.size === 1 ? '' : 's'}`
              : 'Run on entire account'}
          </button>
        </div>
      </div>
    </div>
  );
}

// PacingSparkline removed with the Pacing column — see the note at the table
// header. Reinstate together with a flight-aware pacing figure.

// Score → impact tone (high ≥ 8, medium ≥ 6, else low).
const scoreTone = (s) => (s >= 8 ? 'high' : s >= 6 ? 'med' : 'low');

const CATEGORY_LABEL = {
  keyword_expansion: 'Keyword expansion',
  competitor_conquest: 'Competitor conquest',
  audience_expansion: 'Audience expansion',
  geo_targeting: 'Geo targeting',
  messaging_angle: 'Messaging angle',
  funnel_stage: 'Funnel stage',
  seasonal_event: 'Seasonal / event',
  cost_optimization: 'Cost optimization',
  product_vertical: 'Product vertical',
  retargeting_reengagement: 'Retargeting',
  // LinkedIn perf-agent categories
  seniority_targeting: 'Seniority targeting',
  industry_vertical: 'Industry vertical',
  account_based: 'Account-based',
  skills_interest: 'Skills / interest',
  company_size: 'Company size',
  // Facebook perf-agent categories
  geo: 'Geo segment',
  audience: 'Audience segment',
  daypart: 'Daypart',
  kpi: 'KPI-aligned',
  retargeting: 'Retargeting',
};

/** Pull the entity list out of a (possibly full) dry-run result payload. The
 *  manual run_multi returns a flat, campaign-tagged `llm_plan`; fall back to the
 *  created_entities buckets (all three types) for older / single-run shapes. */
function parsePlan(result) {
  if (!result) return [];
  if (Array.isArray(result.llm_plan)) return result.llm_plan;
  const ce = result.created_entities || {};
  return [
    ...(ce.search_ad_groups || []),
    ...(ce.pmax_search_themes || []),
    ...(ce.demandgen_targeting || []),
    // Facebook single-run fallback: the agent returns created_adsets (each with
    // a nested `plan`) rather than a created_entities bucket.
    ...(result.created_adsets || []),
  ].map(a => a.plan || a);
}

// ─── Search ad group body — keywords + CPC (editable) ───────────────────────
function SearchAdGroupBody({ rec, onChange, applied, sym = '$' }) {
  // Editing a keyword's TEXT invalidates its fetched metrics (they describe the
  // old term), so drop them; changing match type leaves them intact.
  const setKw = (i, patch) => onChange({
    keywords: rec.keywords.map((k, idx) => {
      if (idx !== i) return k;
      const next = { ...k, ...patch };
      if (patch.keyword !== undefined && patch.keyword !== k.keyword) {
        for (const mk of KW_METRIC_KEYS) delete next[mk];
      }
      return next;
    }),
  });
  const removeKw = (i) => onChange({ keywords: rec.keywords.filter((_, idx) => idx !== i) });
  const addKw = () => onChange({ keywords: [...rec.keywords, { keyword: '', match_type: 'PHRASE' }] });
  const vol = recVolume(rec);
  return (
    <>
      <div className="pg-rec-kw-head">
        <span>
          Keywords
          {vol && (
            <span
              className="pg-rec-vol-total"
              title={`Combined Google search demand across ${vol.withData} of ${vol.total_count} suggested keywords`}
            >
              {fmtVolume(vol.total)}/mo
            </span>
          )}
        </span>
        <span className="pg-rec-cpc">
          CPC&nbsp;{sym}
          <input
            type="number" step="0.05" min="0"
            value={rec.cpc_bid ?? ''}
            placeholder="auto"
            disabled={applied}
            onChange={e => onChange({ cpc_bid: e.target.value === '' ? null : Number(e.target.value) })}
          />
        </span>
      </div>
      <div className="pg-rec-kw-list">
        {rec.keywords.map((k, i) => (
          <div className="pg-rec-kw" key={i}>
            <input
              className="pg-rec-kw-text"
              value={k.keyword}
              placeholder="keyword"
              disabled={applied}
              onChange={e => setKw(i, { keyword: e.target.value })}
            />
            {k.keyword ? <KwMetrics k={k} sym={sym} /> : null}
            <select
              className="pg-rec-kw-mt"
              value={k.match_type || 'BROAD'}
              disabled={applied}
              onChange={e => setKw(i, { match_type: e.target.value })}
            >
              {MATCH_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {!applied && <button className="pg-rec-kw-rm" onClick={() => removeKw(i)} title="Remove">×</button>}
          </div>
        ))}
        {!applied && <button className="pg-rec-kw-add" onClick={addKw}>+ keyword</button>}
      </div>
    </>
  );
}

// ─── PMax body — search themes appended to an existing asset group ──────────
function PmaxThemesBody({ rec, onChange, applied, sym = '$' }) {
  const themes = rec.search_themes || [];
  // Volume lives in a parallel array (search_theme_metrics) because the themes
  // array itself must stay plain strings. Keep the two aligned on every edit,
  // and drop a theme's metrics when its text changes.
  const metrics = rec.search_theme_metrics || [];
  const metricFor = (i, t) => {
    const m = metrics[i];
    return (m && m.search_theme === t) ? m : null;
  };
  const setTheme = (i, val) => onChange({
    search_themes: themes.map((t, idx) => idx === i ? val : t),
    search_theme_metrics: metrics.map((m, idx) => idx === i ? { search_theme: val } : m),
  });
  const removeTheme = (i) => onChange({
    search_themes: themes.filter((_, idx) => idx !== i),
    search_theme_metrics: metrics.filter((_, idx) => idx !== i),
  });
  const addTheme = () => onChange({
    search_themes: [...themes, ''],
    search_theme_metrics: [...metrics, { search_theme: '' }],
  });
  const vol = recVolume(rec);
  return (
    <div className="pg-rec-themes">
      <div className="pg-rec-kw-head">
        <span>
          Search themes
          {vol && (
            <span
              className="pg-rec-vol-total"
              title={`Combined Google search demand across ${vol.withData} of ${vol.total_count} suggested themes`}
            >
              {fmtVolume(vol.total)}/mo
            </span>
          )}
        </span>
      </div>
      <div className="pg-rec-kw-list">
        {themes.map((t, i) => {
          const m = metricFor(i, t);
          return (
          <div className="pg-rec-kw" key={i}>
            <input
              className="pg-rec-kw-text"
              value={t}
              placeholder="search theme"
              disabled={applied}
              onChange={e => setTheme(i, e.target.value)}
            />
            {t && m ? <KwMetrics k={m} sym={sym} /> : null}
            {!applied && <button className="pg-rec-kw-rm" onClick={() => removeTheme(i)} title="Remove">×</button>}
          </div>
          );
        })}
        {!applied && <button className="pg-rec-kw-add" onClick={addTheme}>+ theme</button>}
      </div>
    </div>
  );
}

// ─── DemandGen body — audience targeting set on an existing ad group ────────
function DemandGenTargetingBody({ rec, onChange, applied }) {
  const t = rec.targeting || {};
  const patchDim = (key, next) => onChange({ targeting: { ...t, [key]: next } });
  const removeVal = (key, val) => patchDim(key, (t[key] || []).filter(v => v !== val));
  const addVal = (key, val) => { if (val && !(t[key] || []).includes(val)) patchDim(key, [...(t[key] || []), val]); };

  const interests = t.interests || [];
  const setInterest = (i, val) => patchDim('interests', interests.map((x, idx) => idx === i ? val : x));
  const removeInterest = (i) => patchDim('interests', interests.filter((_, idx) => idx !== i));
  const addInterest = () => patchDim('interests', [...interests, '']);

  return (
    <div className="pg-rec-targeting">
      {TARGETING_DIMS.map(dim => {
        const vals = t[dim.key] || [];
        const remaining = dim.options.filter(o => !vals.includes(o));
        if (applied && vals.length === 0) return null;
        return (
          <div className="pg-tgt-dim" key={dim.key}>
            <span className="pg-tgt-label">{dim.label}</span>
            <div className="pg-tgt-chips">
              {vals.map(v => (
                <span className="pg-tgt-chip" key={v}>
                  {fmtEnum(v)}
                  {!applied && <button className="pg-tgt-rm" onClick={() => removeVal(dim.key, v)} title="Remove">×</button>}
                </span>
              ))}
              {vals.length === 0 && applied && <span className="pg-tgt-none">—</span>}
              {!applied && remaining.length > 0 && (
                <select
                  className="pg-tgt-add"
                  value=""
                  onChange={e => { addVal(dim.key, e.target.value); e.target.value = ''; }}
                >
                  <option value="">+ add</option>
                  {remaining.map(o => <option key={o} value={o}>{fmtEnum(o)}</option>)}
                </select>
              )}
            </div>
          </div>
        );
      })}

      <div className="pg-tgt-dim pg-tgt-interests">
        <span className="pg-tgt-label">Interests</span>
        <div className="pg-rec-kw-list">
          {interests.map((iv, i) => (
            <div className="pg-rec-kw" key={i}>
              <input
                className="pg-rec-kw-text"
                value={iv}
                placeholder="interest (e.g. banking)"
                disabled={applied}
                onChange={e => setInterest(i, e.target.value)}
              />
              {!applied && <button className="pg-rec-kw-rm" onClick={() => removeInterest(i)} title="Remove">×</button>}
            </div>
          ))}
          {!applied && <button className="pg-rec-kw-add" onClick={addInterest}>+ interest</button>}
        </div>
      </div>
    </div>
  );
}

// ─── LinkedIn campaign body — read-only targeting facets + delivery ─────────
// The LinkedIn perf agent resolves human-readable facets → LinkedIn URNs at
// apply time, so we display the facets it chose (not editable here) plus the
// objective / optimization / daily budget it recommends.
function LinkedInCampaignBody({ rec }) {
  const facets = Object.entries(rec.targetingConfig || {})
    .filter(([, v]) => v != null && String(v).trim() !== '');
  const budget = rec.dailyBudget && rec.dailyBudget.amount
    ? `${rec.dailyBudget.amount} ${rec.dailyBudget.currencyCode || ''}`.trim()
    : null;
  return (
    <div className="pg-rec-body pg-rec-linkedin">
      {facets.length > 0 ? (
        <div className="pg-rec-facets">
          {facets.map(([k, v]) => (
            <div className="pg-rec-facet" key={k}>
              <span className="pg-rec-facet-key">{k.replace(/_/g, ' ')}</span>
              <span className="pg-rec-facet-val">{String(v)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="pg-rec-empty">No targeting facets</div>
      )}
      <div className="pg-rec-delivery">
        {rec.objectiveType && <span className="pg-rec-chip">{rec.objectiveType}</span>}
        {rec.optimizationTargetType && <span className="pg-rec-chip">{rec.optimizationTargetType}</span>}
        {budget && <span className="pg-rec-chip">{budget}/day</span>}
      </div>
    </div>
  );
}

// ─── Facebook ad set body — read-only targeting modifications + goal ────────
// The FB perf agent clones the campaign's template ad set and layers
// targeting_modifications on top at apply time, so we display what it changes
// (not editable here) plus the optimization goal it recommends. An empty
// modifications set means the new ad set copies the template targeting as-is.
function FacebookAdSetBody({ rec }) {
  const mods = rec.targeting_modifications || {};
  const fmtVal = (v) => {
    if (Array.isArray(v)) {
      return v.map(x => (x && typeof x === 'object') ? (x.name || x.id || JSON.stringify(x)) : String(x)).join(', ');
    }
    if (v && typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  const facets = Object.entries(mods)
    .filter(([, v]) => v != null && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0));
  return (
    <div className="pg-rec-body pg-rec-fb_ad_set">
      {facets.length > 0 ? (
        <div className="pg-rec-facets">
          {facets.map(([k, v]) => (
            <div className="pg-rec-facet" key={k}>
              <span className="pg-rec-facet-key">{k.replace(/_/g, ' ')}</span>
              <span className="pg-rec-facet-val">{fmtVal(v)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="pg-rec-empty">Copies the template ad set's targeting</div>
      )}
      {rec.optimization_goal && (
        <div className="pg-rec-delivery">
          <span className="pg-rec-chip">{rec.optimization_goal}</span>
        </div>
      )}
    </div>
  );
}

// ─── Recommendation card (editable) ────────────────────────────────────────
function RecommendationCard({ rec, appliedLink, onChange, onToggle, onReview, currencyCode }) {
  const tone = scoreTone(rec.score || 0);
  // Currency symbol for the money bits in the card bodies (CPC bid label,
  // keyword bid tooltips). Google Ads reports these in the account's billing
  // currency — not necessarily USD — so honor the campaign's currency_code.
  const sym = curSym(currencyCode);
  // Applied recommendations are locked: read-only, deselected, and excluded
  // from Apply so they can't create duplicate entities.
  const applied = !!rec.applied;
  const type = rec.type || 'search_ad_group';
  const appliedTitle = type === 'pmax_search_themes' ? 'Search themes added to the asset group in Google Ads'
    : type === 'demandgen_targeting' ? 'Targeting applied to the ad group in Google Ads'
    : type === 'linkedin_campaign' ? 'Created as a DRAFT campaign in LinkedIn'
    : type === 'fb_ad_set' ? 'Created as a paused ad set in Facebook'
    : 'Created as a paused ad group in Google Ads';

  // Type tag + score (or applied badge) + category — shown on their own meta
  // row below the name (the name leads the first line at full width for every
  // type: an editable input for search, a read-only entity name for PMax /
  // DemandGen).
  const metaBadges = (
    <>
      <span className="pg-rec-type" title={REC_TYPE_LABEL[type] || type}>{REC_TYPE_TAG[type] || type}</span>
      {applied
        ? <span className="pg-applied-badge" title={appliedTitle}>✓ Applied</span>
        : <span className={`pg-impact impact-${tone}`} title="Agent score (1–10)">★ {rec.score ?? '—'}</span>}
      {rec.category && <span className="pg-rec-cat">{CATEGORY_LABEL[rec.category] || rec.category}</span>}
    </>
  );

  return (
    <div className={`pg-rec pg-rec-${type} ${rec.selected ? 'selected' : ''} ${applied ? 'applied' : ''}`}>
      {applied && appliedLink && (
        <a className="pg-rec-applied-link" href={appliedLink} target="_blank" rel="noopener noreferrer">
          {adsLinkLabel()}
        </a>
      )}
      <div className="pg-rec-head">
        <button
          className="pg-rec-check"
          onClick={applied ? undefined : onToggle}
          disabled={applied || requiresMomentReview(rec)}
          title={requiresMomentReview(rec) ? 'Review details to select targeting and ads' : applied ? 'Applied' : rec.selected ? 'Deselect' : 'Select'}
        >{(rec.selected || applied) ? '✓' : ''}</button>
        {(type === 'search_ad_group' || type === 'linkedin_campaign' || type === 'fb_ad_set')
          ? <input
              className="pg-rec-name"
              value={rec.name}
              placeholder={type === 'linkedin_campaign' ? 'Campaign name' : type === 'fb_ad_set' ? 'Ad set name' : 'Ad group name'}
              disabled={applied || requiresMomentReview(rec)}
              onChange={e => onChange({ name: e.target.value })}
            />
          // PMax themes / DemandGen targeting augment an EXISTING entity, so its
          // name is read-only and leads the first line at full width.
          : <span className="pg-rec-name pg-rec-name-fixed" title={rec.name}>{rec.name || '(unnamed)'}</span>}
      </div>

      <div className="pg-rec-meta">{metaBadges}</div>

      {rec._campaignName && (
        <div className="pg-rec-camp" title={rec._campaignName}>↳ {rec._campaignName}</div>
      )}

      <textarea
        className="pg-rec-rationale"
        rows={2}
        value={rec.rationale || ''}
        placeholder="Why this recommendation"
        disabled={applied || requiresMomentReview(rec)}
        onChange={e => onChange({ rationale: e.target.value })}
      />

      {type === 'search_ad_group' && <><SearchAdGroupBody rec={rec} onChange={onChange} applied={applied || requiresMomentReview(rec)} sym={sym} />{rec._source === 'cron' && <button className="pg-btn" onClick={onReview}>More details</button>}</>}
      {type === 'pmax_search_themes' && <><PmaxThemesBody rec={rec} onChange={onChange} applied={applied || requiresMomentReview(rec)} sym={sym} />{rec._source === 'cron' && <button className="pg-btn" onClick={onReview}>More details</button>}</>}
      {type === 'demandgen_targeting' && <><DemandGenTargetingBody rec={rec} onChange={onChange} applied={true} /><button className="pg-btn" onClick={onReview}>More details</button></>}
      {type === 'linkedin_campaign' && <LinkedInCampaignBody rec={rec} />}
      {type === 'fb_ad_set' && <FacebookAdSetBody rec={rec} />}
    </div>
  );
}

// Recommendation CATEGORY — which run replaces a card. Mirrors the
// backend REC_CATEGORY (campaignGrid.js). Persisted on the DB `source` column
// (surfaced as rec._source here); the value strings are HISTORICAL and encode
// the CATEGORY, NOT the cronjob-vs-button invocation — those are orthogonal: a
// weekly-cron Moments run and a manual "Run Moments" BOTH produce
// REC_CATEGORY.MOMENTS and both land in Moments (overwriting each other there).
// (rec._source is kept distinct from rec.category, which is the rec's own label.)
const REC_CATEGORY = Object.freeze({ MOMENTS: 'cron', RECOMMENDATIONS: 'manual' });
// Keyed on the exact field the backend scopes its delete by, so a card shown in
// a category is always precisely what that category's next run will replace.
const isMomentRec = (r) => r?._source === REC_CATEGORY.MOMENTS;

// ─── Main page ──────────────────────────────────────────────────────────────
function PerfGridPage() {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  const [user] = useState(() => getUserData());
  const userId = user?.userId || user?.id;
  // The real super-admin id, preserved across impersonation: while a super
  // admin impersonates, the stored user is the impersonated target, so the
  // super-admin id lives in impersonatedBy (only super admins can impersonate).
  // Mirrors CampaignDetailPage's superAdminUserId derivation.
  const superAdminUserId = user?.isImpersonating ? user?.impersonatedBy : (user?.userId || user?.id);
  // Show "Run Moments" whenever the real actor is a super admin — including
  // while impersonating (isImpersonating ⇒ a super admin is driving). The
  // endpoint re-validates super-admin against superAdminUserId, so this gate
  // is UX only.
  const userIsSuperAdmin = isSuperAdmin(user) || Boolean(user?.isImpersonating && user?.impersonatedBy);
  const [runningTriggers, setRunningTriggers] = useState(false); // manual trigger run in flight (super admin)
  const [contextOpen, setContextOpen] = useState(false);
  const [momentsPickerOpen, setMomentsPickerOpen] = useState(false); // "Run Moments" scope modal
  const [triggersLastRunAt, setTriggersLastRunAt] = useState(null); // ms — last manual trigger-driven run
  const [triggersStartedAt, setTriggersStartedAt] = useState(null); // ms — current trigger run start (for elapsed)
  const [momentsError, setMomentsError] = useState(null); // last trigger-run error
  const [nowTick, setNowTick] = useState(0); // re-render clock for the live "elapsed" label while triggers run
  const { getSetting } = useAccountSettings(accountId, user);
  const applyButtonDisabled = getSetting('disable-v3-apply-recommendation-button') === 'true';

  const [campaigns, setCampaigns] = useState([]);
  // True from the FIRST render when there's an account to load: the fetch starts
  // in an effect, so a `false` initial value made the page paint "No campaigns
  // found." before it had asked the server anything.
  const [loading, setLoading] = useState(Boolean(accountId)); // loading campaign list
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');

  // Agent state
  const [filterId, setFilterId] = useState(null);    // optional: filter recs to one campaign
  const [running, setRunning] = useState(false);     // dry-run batched run(s) in flight
  const [analyzingCount, setAnalyzingCount] = useState(0); // # campaigns being analyzed
  const [applying, setApplying] = useState(false);   // apply in flight
  const [recs, setRecs] = useState([]);              // editable recs, tagged with _campaignId/_campaignName
  const [applyLog, setApplyLog] = useState([]);   // dismissable list of applied-recommendation confirmations
  const [impacts, setImpacts] = useState([]);      // durable observed before/after history
  const [impactsLoading, setImpactsLoading] = useState(Boolean(accountId));
  const [gridView, setGridView] = useState('campaigns'); // campaigns | impacts
  const [recsLastRefreshedAt, setRecsLastRefreshedAt] = useState(null); // ms — last recommendation refresh
  const [recsError, setRecsError] = useState(null);  // last recommendation-run error (surfaced in the panel)
  // Both sources load from one persisted-recommendations request. Start in
  // loading state so the empty state cannot appear before the response.
  const [recsLoading, setRecsLoading] = useState(Boolean(accountId));

  const pollTimers = useRef(new Map());
  const autoRan = useRef(false);

  // Time filter — scopes the METRICS fetches only (grid load + Refresh
  // Metrics ask the reporting APIs for this window). Moments and
  // Recommendations runs are deliberately NOT windowed: a moment trigger
  // evaluates its own lookback semantics, and narrowing its data because a
  // viewer had a UI filter on would change what fires.
  // Restored from the last visit (per account) — same persistence as the
  // Campaign Grid, same reason: the picker reset to "All time" on every load
  // while the cached dataset kept the last successful pull's window.
  // NO time picker on Triggers. This page never pulls its own windows — it
  // READS the persisted campaign-grid dataset, and shows the window that
  // dataset actually covers (the stamps the Campaign Grid's last pull wrote).
  // Changing the window happens on the Campaign Grid; this page just reports
  // what it is looking at.

  // ─── Load campaigns (perf-agent platforms: Google Ads + LinkedIn + Facebook) ─
  const loadCampaigns = async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      // Unwindowed on purpose: this page reads the campaign-grid dataset as
      // the Campaign Grid last persisted it. The dataset-window label below
      // says which range those cached numbers cover.
      const r = await api.get(`/api/v2/campaign-grid`, { params: { accountId, userId } });
      const all = r.data.campaigns || [];
      setCampaigns(all.filter(c => agentTypeForDestination(c.destination)));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadCampaigns(); }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImpacts = async () => {
    if (!accountId) { setImpacts([]); setImpactsLoading(false); return; }
    setImpactsLoading(true);
    try {
      const r = await api.get('/api/v2/campaign-grid/change-impacts', { params: { accountId, userId } });
      setImpacts(r.data.impacts || []);
    } catch (_) {
      // The migration can roll out independently of the UI. Keep the campaign
      // grid usable if the impact table is not present yet.
      setImpacts([]);
    } finally {
      setImpactsLoading(false);
    }
  };
  useEffect(() => { loadImpacts(); }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Map a persisted recommendation row (campaign_grid_recommendations) to a card.
  const mapPersistedRec = (row, i) => ({
    _key: `db:${row.id || i}`,
    _campaignId: row.campaign_id,
    _campaignName: row.campaign_name || '',
    // Destination drives the apply agentType (g-ads vs LinkedIn); the rec row
    // stores it as `platform`.
    _destination: row.platform || null,
    // The moment (trigger) that produced this recommendation, when it came from
    // the Moments Engine trigger chain. Empty for on-demand agent runs.
    _triggerId: row.trigger_id || null,
    _triggerName: row.trigger_name || '',
    // Preserve the DB source so refreshes replace only their own category.
    _source: row.source || null,
    _appliedResource: row.applied_resource_name || null,
    selected: false,   // deselected by default; the user opts in per recommendation
    applied: !!row.applied,
    type: row.type || 'search_ad_group',
    name: row.name || '',
    category: row.category || '',
    score: row.score != null ? Number(row.score) : 0,
    rationale: row.rationale || '',
    // search_ad_group
    cpc_bid: row.cpc_bid != null ? Number(row.cpc_bid) : null,
    keywords: Array.isArray(row.keywords)
      ? row.keywords.map(k => typeof k === 'string'
          ? { keyword: k, match_type: 'BROAD' }
          : { keyword: k.keyword || '', match_type: k.match_type || 'BROAD', ...pickKwMetrics(k) })
      : [],
    // pmax_search_themes
    asset_group_id: row.asset_group_id || null,
    asset_group_name: row.asset_group_name || '',
    search_themes: Array.isArray(row.search_themes) ? row.search_themes.map(String) : [],
    // Per-theme search volume, parallel to search_themes (own column since the
    // themes array must stay plain strings for the editor + apply path).
    search_theme_metrics: Array.isArray(row.search_theme_metrics)
      ? row.search_theme_metrics.map(m => ({ search_theme: String(m?.search_theme ?? ''), ...pickKwMetrics(m) }))
      : [],
    // demandgen_targeting
    ad_group_id: row.ad_group_id || null,
    ad_group_name: row.ad_group_name || '',
    targeting: normTargeting(row.targeting),
    // linkedin_campaign — the human-readable facet map is stored in the shared
    // `targeting` column (see persistRecommendations); objective / budget aren't
    // persisted, so apply inherits them from the group's template campaign.
    targetingConfig: (row.type === 'linkedin_campaign' && row.targeting && typeof row.targeting === 'object')
      ? row.targeting : {},
    objectiveType: null,
    optimizationTargetType: null,
    dailyBudget: null,
    // fb_ad_set — persistRecommendations stores { targeting_modifications,
    // optimization_goal } in the shared `targeting` column; unpack both.
    targeting_modifications: (row.type === 'fb_ad_set' && row.targeting && typeof row.targeting === 'object')
      ? (row.targeting.targeting_modifications || {}) : {},
    optimization_goal: (row.type === 'fb_ad_set' && row.targeting && typeof row.targeting === 'object')
      ? (row.targeting.optimization_goal || null) : null,
  });

  // Load persisted recommendations (written by the weekly cron + manual re-runs).
  // Returns the count so the caller can decide whether to auto-run.
  const loadRecommendations = async () => {
    if (!accountId) {
      setRecsLoading(false);
      return 0;
    }
    setRecsLoading(true);
    try {
      const r = await api.get(`/api/v2/campaign-grid/recommendations`, { params: { accountId, userId } });
      const rows = r.data.recommendations || [];
      setRecs(rows.map(mapPersistedRec));
      setRecsLastRefreshedAt(r.data.lastRefreshedAt || null);
      return rows.length;
    } catch (_) {
      return 0;
    } finally {
      // finally, not after the try: a failed fetch must stop the spinner too,
      // or the list spins forever on an error the caller swallows.
      setRecsLoading(false);
    }
  };

  useEffect(() => () => {
    for (const h of pollTimers.current.values()) clearInterval(h);
  }, []);

  // Tick a clock every second while a manual trigger run is in flight so the
  // "still running (Xm Ys elapsed)" status counts up smoothly (a coarser tick
  // makes the seconds look frozen/jumpy). Stops (and clears) as soon as it ends.
  useEffect(() => {
    if (!runningTriggers) return undefined;
    setNowTick(Date.now());
    const h = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(h);
  }, [runningTriggers]);

  // ─── "Run Moments" (super admin) — manually run the Moments (trigger agent)
  //     pull for THIS account: the same flow the weekly cron runs (evaluate
  //     moment triggers → chain fired ones into the perf agent), populating the
  //     Moments section (each card tagged with the moment/trigger that drove
  //     it). Not the empty-guidance 'Run Recommendations' run. (The function is
  //     still named runTriggers — a Moment is a moment-trigger.)
  //
  //     `campaignIds` (from the scope modal) narrows the run to those campaigns;
  //     null/empty runs the whole account and sends NO campaignIds, so the
  //     account-wide request is byte-identical to what it always was.
  const runTriggers = async (campaignIds = null) => {
    if (!accountId || runningTriggers) return;
    const scoped = Array.isArray(campaignIds) && campaignIds.length > 0;
    setRunningTriggers(true);
    setTriggersStartedAt(Date.now());
    setMomentsError(null);
    try {
      const r = await api.post(`/api/v2/campaign-grid/run-triggers`, {
        accountId,
        userId: superAdminUserId,
        ...(scoped ? { campaignIds } : {}),
      });
      const { dispatched = 0, runIds = [] } = r.data || {};
      if (dispatched === 0 || runIds.length === 0) {
        // Nothing dispatched — leave the existing Moments cards in place (we
        // must NOT have cleared them before this check, or the section would
        // blank out with nothing to reload).
        setToast({
          msg: scoped
            ? 'No grid-eligible campaigns among the selected ones'
            : 'No grid-eligible campaigns to run triggers for this account',
        });
        return;
      }
      // Runs are dispatched — clear only trigger-driven cards now (they'll be
      // reloaded when the runs finish); leave on-demand Recommendations intact.
      // A SCOPED run only replaces the selected campaigns' moments, so clear
      // just those cards — wiping the whole section would blank moments this
      // run is never going to regenerate.
      const scopeSet = scoped ? new Set(campaignIds) : null;
      setRecs(prev => prev.filter(r => !isMomentRec(r) || (scopeSet && !scopeSet.has(r._campaignId))));
      setApplyLog([]);
      setToast({
        msg: scoped
          ? `Running triggers for ${campaignIds.length} campaign${campaignIds.length === 1 ? '' : 's'}…`
          : `Running triggers for ${dispatched} target(s)…`,
      });
      // Poll each dispatched run; finalizeRun persists the trigger-driven
      // recommendations (source='cron') as each completes.
      const runErrors = [];
      // A Moments run plans every campaign sequentially, so give it a long 2h
      // cap rather than the 10-min default — but never poll forever. If a run
      // is still going at the cap, background it gracefully: stop polling and
      // load whatever landed; the backend run keeps going and the rest appear
      // on the next reload (no hard "timed out" error, and the button re-enables
      // via the finally below). Timers are also cleared on unmount.
      const TRIGGER_RUN_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
      let backgrounded = false;
      await Promise.all(runIds.map((id) => new Promise((resolve) => {
        pollRun(
          id,
          (doneRun) => {
            if (doneRun.status === 'error') runErrors.push(describeRunError(doneRun));
            resolve();
          },
          TRIGGER_RUN_TIMEOUT_MS,
          () => { backgrounded = true; resolve(); },
        );
      })));
      // Load the persisted recommendations into the existing cards — identical
      // to how the weekly-cron output renders.
      const persisted = await loadRecommendations();
      setTriggersLastRunAt(Date.now());
      if (backgrounded) {
        // Not an error — some runs are still processing server-side.
        setToast({ msg: 'Still processing in the background — reload to see the rest.' });
      } else if (runErrors.length > 0) {
        const uniq = [...new Set(runErrors)];
        const summary = uniq.slice(0, 2).join('; ') + (uniq.length > 2 ? '; …' : '');
        setMomentsError(summary);
        setToast({ msg: `Trigger run failed: ${summary}`, err: true });
      } else {
        setToast({ msg: persisted === 0 ? 'No recommendations from fired triggers' : 'Recommendations ready' });
      }
    } catch (e) {
      setToast({ msg: e?.response?.data?.error || 'Failed to run triggers' });
    } finally {
      setRunningTriggers(false);
      setTriggersStartedAt(null);
    }
  };

  // ─── Reload = trigger a live metrics pull (POST /refresh-metrics) then
  //     reload the grid — not just re-read the cache. Mirrors CampaignGridPage. ─
  // No metrics pull from this page: the Campaign Grid owns pulls (its window
  // picker blanks + re-pulls); Triggers only reads what it persisted.

  useEffect(() => {
    if (!toast) return;
    const h = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(h);
  }, [toast]);

  const filtered = useMemo(() => {
    if (!search.trim()) return campaigns;
    const q = search.toLowerCase();
    return campaigns.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.destination?.toLowerCase().includes(q) ||
      c.folder_name?.toLowerCase().includes(q)
    );
  }, [campaigns, search]);

  // The time slot the RENDERED dataset covers — from the rows' metrics_window
  // stamps (the last successful pull's filter). Null until anything is pulled.
  const dataWindow = useMemo(() => latestPullWindow(filtered), [filtered]);

  // ─── Poll a run until terminal, then hand the result to `onDone` ─────────
  // timeoutMs caps how long we poll (always finite — never poll forever). The
  // quick flows (Run Recommendations, Apply) use the ~10 min default; Run
  // Moments passes a long 2h cap. On hitting the cap we stop polling and, if an
  // onTimeout handler is given, hand off to it (graceful "still running in the
  // background" — the backend run keeps going and its results land on the next
  // reload); otherwise we surface the legacy "timed out" error via onDone.
  // Timers are also cleared on unmount (see the cleanup effect above).
  const pollRun = (runId, onDone, timeoutMs = 600000, onTimeout = null) => {
    if (pollTimers.current.has(runId)) return;
    const handle = setInterval(async () => {
      try {
        const r = await api.get(`/api/v2/campaign-grid/runs/${runId}`, { params: { accountId, userId } });
        const run = r.data.run;
        if (run && (run.status === 'success' || run.status === 'error')) {
          clearInterval(handle);
          pollTimers.current.delete(runId);
          onDone(run);
        }
      } catch (_) { /* keep polling through transient errors */ }
    }, 4000);
    pollTimers.current.set(runId, handle);
    setTimeout(() => {
      if (pollTimers.current.has(runId)) {
        clearInterval(pollTimers.current.get(runId));
        pollTimers.current.delete(runId);
        if (onTimeout) onTimeout(runId);
        else onDone({ status: 'error', error: 'Timed out waiting for the agent' });
      }
    }, Number.isFinite(timeoutMs) ? timeoutMs : 600000);
  };

  const mapRec = (p, c, key) => ({
    _key: key,
    _campaignId: c?.id ?? null,
    _campaignName: c?.name || p.campaign_name || '',
    _destination: c?.destination ?? null,
    _triggerId: p.trigger_id ?? null,
    _triggerName: p.trigger_name || '',
    // Streamed live cards: trigger-tagged items are Moments, the rest Recommendations.
    _source: p.trigger_id ? REC_CATEGORY.MOMENTS : REC_CATEGORY.RECOMMENDATIONS,
    _appliedResource: null,
    selected: false,   // deselected by default; the user opts in per recommendation
    applied: false,
    type: p.type || 'search_ad_group',
    name: p.name || p.asset_group_name || p.ad_group_name || '',
    category: p.category || '',
    score: p.score ?? 0,
    rationale: p.rationale || '',
    // search_ad_group
    cpc_bid: p.cpc_bid ?? null,
    keywords: (p.keywords || []).map(k =>
      typeof k === 'string'
        ? { keyword: k, match_type: 'BROAD' }
        : { keyword: k.keyword || '', match_type: k.match_type || 'BROAD', ...pickKwMetrics(k) }
    ),
    // pmax_search_themes
    asset_group_id: p.asset_group_id ?? null,
    asset_group_name: p.asset_group_name || '',
    search_themes: Array.isArray(p.search_themes) ? p.search_themes.map(String) : [],
    search_theme_metrics: Array.isArray(p.search_theme_metrics)
      ? p.search_theme_metrics.map(m => ({ search_theme: String(m?.search_theme ?? ''), ...pickKwMetrics(m) }))
      : [],
    // demandgen_targeting
    ad_group_id: p.ad_group_id ?? null,
    ad_group_name: p.ad_group_name || '',
    targeting: normTargeting(p.targeting),
    // linkedin_campaign — read-only display of the LLM's targeting facets +
    // delivery settings; carried verbatim into the apply_plan on apply.
    targetingConfig: (p.targetingConfig && typeof p.targetingConfig === 'object') ? p.targetingConfig : {},
    objectiveType: p.objectiveType || null,
    optimizationTargetType: p.optimizationTargetType || null,
    dailyBudget: (p.dailyBudget && typeof p.dailyBudget === 'object') ? p.dailyBudget : null,
    // fb_ad_set — the LLM's targeting modifications + optimization goal, carried
    // verbatim into the apply_plan on apply.
    targeting_modifications: (p.targeting_modifications && typeof p.targeting_modifications === 'object') ? p.targeting_modifications : {},
    optimization_goal: p.optimization_goal || null,
  });

  // ─── Auto-run the agent (dry-run, empty message) across all active
  //     campaigns of every supported platform. Campaigns are grouped by
  //     customer / ad account and each group's IDs go to the agent in ONE
  //     batched run (run_multi) — not one run per campaign. ────────────────
  const runAll = async () => {
    // Any active campaign whose destination has a perf agent (Google Ads,
    // LinkedIn, or Facebook). Each campaign's agentType is chosen by destination
    // so every platform's agent runs — respectively and concurrently — in one pass.
    const targets = campaigns.filter(c => agentTypeForDestination(c.destination) && isActiveCampaign(c));
    if (running || targets.length === 0) return;
    setRunning(true);
    // Clear only on-demand cards — leave trigger-driven cards intact so
    // the two results never clobber each other.
    setRecs(prev => prev.filter(r => isMomentRec(r)));
    setApplyLog([]);
    setRecsError(null);
    setAnalyzingCount(targets.length);
    const runErrors = [];

    // One run per (agentType, advertiser_id): all of a Google Ads customer's
    // campaigns batch into one g-ads run; all of a LinkedIn ad account's
    // campaign groups into one LinkedIn run; all of a Facebook ad account's
    // campaigns into one FB run. Keying on agentType too keeps platforms from
    // ever sharing a run.
    const byCustomer = new Map();
    for (const c of targets) {
      const agentType = agentTypeForDestination(c.destination);
      const key = `${agentType}::${c.advertiser_id || 'unknown'}`;
      if (!byCustomer.has(key)) byCustomer.set(key, { agentType, group: [] });
      byCustomer.get(key).group.push(c);
    }

    await Promise.all([...byCustomer.values()].map(async ({ agentType, group }) => {
      const rep = group[0];                       // representative — provides channel_config
      const platformToInternal = {};
      const platformIds = [];
      for (const c of group) {
        for (const pid of collectPlatformIds(c)) {
          platformToInternal[pid] = c;
          platformIds.push(pid);
        }
      }
      if (platformIds.length === 0) return;
      try {
        const r = await api.post(`/api/v2/campaign-grid/${rep.id}/run-agent`, {
          agentType,
          message: '',                // empty guidance — let the agent find gaps on its own
          dry_run: true,
          campaign_ids: platformIds,  // all this customer's campaigns in one run
          accountId, userId,
        });
        const run = r.data.run;
        await new Promise((resolve) => {
          pollRun(run.id, (doneRun) => {
            if (doneRun.status === 'error') {
              runErrors.push(describeRunError(doneRun));
            } else {
              const plan = parsePlan(doneRun.result)
                .filter(p => REC_TYPE_LABEL[p.type || 'search_ad_group']);
              if (plan.length) {
                setRecs(prev => [...prev, ...plan.map((p, i) => {
                  // g-ads run_multi tags each item with campaign_id; LinkedIn
                  // run_multi tags campaign_group_id (== the platform id we sent).
                  const ext = String(p.campaign_id ?? p.campaign_group_id ?? '');
                  const internal = platformToInternal[ext] || rep;
                  return mapRec(p, internal, `${run.id}:${i}`);
                })]);
              }
            }
            resolve();
          });
        });
      } catch (e) { /* one customer's run failed; others continue */ }
    }));

    setRunning(false);
    // The runs persisted their recommendations (source='manual') as each one
    // finalized. Swap the streamed live cards for the persisted set so every
    // card carries a stable DB id — this unifies the manual + cron display path
    // and lets "applied" marking match the exact row (names collide across
    // PMax / DemandGen recs that share one asset/ad group).
    const persisted = await loadRecommendations();
    // A run just completed — stamp the time regardless of outcome so the label
    // reflects that a run happened (empty or errored), never 'Not refreshed
    // yet'. loadRecommendations() above resets the stamp to the server's value
    // (null when nothing is persisted), so this must come AFTER it. The
    // empty/error detail is conveyed in the panel + toast, not the timestamp.
    setRecsLastRefreshedAt(Date.now());
    if (runErrors.length > 0) {
      // Dedupe (customers often share the same failure) and surface it — both
      // as a toast and in the panel — instead of a misleading 'ready'.
      const uniq = [...new Set(runErrors)];
      const summary = uniq.slice(0, 2).join('; ') + (uniq.length > 2 ? '; …' : '');
      setRecsError(summary);
      setToast({ msg: `Recommendation run failed: ${summary}`, err: true });
    } else {
      setToast({ msg: persisted === 0 ? 'No recommendations found for the active campaigns' : 'Recommendations ready' });
    }
  };

  // On first load: show the persisted recommendations (refreshed by the weekly
  // cron). Do NOT auto-run the agent — running is an explicit user action via
  // the 'Run Recommendations' button (a run fans out an LLM call per campaign).
  useEffect(() => {
    if (autoRan.current) return;
    if (campaigns.length === 0) {
      // Campaign list still in flight — the recs fetch fires the moment it
      // lands, so hold the spinner rather than flashing an empty state. Once the
      // list HAS come back empty there is nothing to load recommendations for,
      // so the spinner has to stop or it spins forever on such an account.
      if (!loading) setRecsLoading(false);
      return;
    }
    autoRan.current = true;
    loadRecommendations();
  }, [campaigns, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRec = (key, patch) =>
    setRecs(prev => prev.map(r => r._key === key ? { ...r, ...patch } : r));
  const toggleRec = (key) =>
    setRecs(prev => prev.map(r => (r._key === key && !r.applied && !requiresMomentReview(r)) ? { ...r, selected: !r.selected } : r));
  // Select-all / deselect-all across the visible (non-applied) moments only.
  const toggleSelectAll = () => {
    const activeKeys = new Set(activeRecs.map(r => r._key));
    const anySelected = activeRecs.some(r => r.selected && !r.applied && !requiresMomentReview(r));
    setRecs(prev => prev.map(r =>
      (activeKeys.has(r._key) && !r.applied && !requiresMomentReview(r)) ? { ...r, selected: !anySelected } : r));
  };

  // Recs shown (optionally filtered to one campaign), ranked by score.
  const displayedRecs = useMemo(() => {
    const list = filterId ? recs.filter(r => r._campaignId === filterId) : recs;
    return [...list].sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [recs, filterId]);
  // Counts and actions share the complete, campaign-filtered Moments list.
  const activeRecs = displayedRecs;
  const selectedCount = activeRecs.filter(r => r.selected && !r.applied && !requiresMomentReview(r)).length;
  const appliedCount = activeRecs.filter(r => r.applied).length;
  const selectableCount = activeRecs.filter(r => !r.applied && !requiresMomentReview(r)).length;

  // Live "elapsed" label for an in-flight trigger run (updated by nowTick).
  const triggersElapsedLabel = useMemo(() => {
    if (!runningTriggers || !triggersStartedAt) return null;
    const ms = Math.max(0, nowTick - triggersStartedAt);
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return m > 0 ? `${m}m` : `${s}s`;
  }, [runningTriggers, triggersStartedAt, nowTick]);

  // "Refreshed Xm ago" for recommendations AND metrics. Metrics freshness comes
  // from the newest cached metrics_fetched_at across the visible campaigns.
  const recsRefreshedLabel = useMemo(() => formatRefreshedAgo(recsLastRefreshedAt), [recsLastRefreshedAt]);
  const triggersRefreshedLabel = useMemo(() => formatRefreshedAgo(triggersLastRunAt), [triggersLastRunAt]);
  const metricsLastRefreshedAt = useMemo(() => newestMetricsFetchedAt(campaigns), [campaigns]);
  const metricsRefreshedLabel = useMemo(() => formatRefreshedAgo(metricsLastRefreshedAt), [metricsLastRefreshedAt]);

  // ─── Apply the selected, edited recommendations ─────────────────────────
  // Search → creates a paused ad group; PMax → appends search themes to the
  // asset group; DemandGen → sets targeting on the ad group. Each campaign is a
  // single channel, so all of its selected recs are the same type.
  const reviewMoment = async rec => {
    try {
      const recommendationId = String(rec._key).startsWith('db:') ? String(rec._key).slice(3) : null;
      if (!recommendationId) throw new Error('Reload moments to review the saved recommendation.');
      if (rec.type === 'pmax_search_themes') { navigate(`/perf-grid?pmaxMoment=${encodeURIComponent(recommendationId)}&campaign=${encodeURIComponent(rec._campaignId)}`); return; }
      if (rec.type === 'search_ad_group') { navigate(`/perf-grid?searchMoment=${encodeURIComponent(recommendationId)}&campaign=${encodeURIComponent(rec._campaignId)}`); return; }
      const { data } = await api.post(`/api/v2/campaign-grid/${rec._campaignId}/moment-plans`, { recommendationId, accountId, userId });
      navigate(`/perf-grid?momentPlan=${data.plan.id}&campaign=${rec._campaignId}`);
    } catch (e) { setToast({ msg: e.response?.data?.error || e.message, err: true }); }
  };

  const applySelected = async () => {
    if (applying) return;
    // Apply only selected moments in the current campaign filter.
    const activeKeys = new Set(activeRecs.map(r => r._key));
    const chosen = recs.filter(r => r.selected && !r.applied && activeKeys.has(r._key));
    if (chosen.length === 0) { setToast({ msg: 'Select at least one recommendation', err: true }); return; }

    // Group by (source campaign, agentType); keep the rec ref alongside each
    // plan item so we can mark the right card "applied" from the per-entity
    // result. Keying on agentType routes each campaign's recs to its platform's
    // agent (g-ads vs LinkedIn) — a campaign only ever has one, but the key
    // keeps the apply_plan and result-parsing platform-correct.
    const byCampaign = new Map();
    for (const r of chosen) {
      if (requiresMomentReview(r)) { await reviewMoment(r); return; }
      const item = buildApplyItem(r);
      if (!item) continue;  // missing keywords / themes / targeting / targetingConfig
      const agentType = agentTypeForDestination(r._destination) || 'g-ads-perf';
      const key = `${agentType}::${r._campaignId}`;
      if (!byCampaign.has(key)) byCampaign.set(key, { campaignId: r._campaignId, agentType, entries: [] });
      byCampaign.get(key).entries.push({ rec: r, item });
    }

    if (byCampaign.size === 0) {
      setToast({ msg: 'Selected recommendations are missing content (keywords / themes / targeting)', err: true });
      return;
    }

    const total = [...byCampaign.values()].reduce((n, g) => n + g.entries.length, 0);
    // Shared across every campaign run started by this click, so overlapping
    // changes in one user action are presented as one apply cohort.
    const applyBatchId = window.crypto?.randomUUID?.();
    setApplying(true);
    setToast({ msg: `Applying ${total} recommendation${total === 1 ? '' : 's'} across ${byCampaign.size} campaign${byCampaign.size === 1 ? '' : 's'}…` });

    let ok = 0, failed = 0;
    const appliedKeys = new Set();
    const logEntries = [];  // per-recommendation confirmations to append to the notification
    const appliedResourceByKey = new Map();  // rec._key → created Google Ads resource name (search ad groups)
    const runIdByKey = new Map();             // rec._key → successful apply run (impact provenance)
    await Promise.all([...byCampaign.values()].map(async ({ campaignId, agentType, entries }) => {
      const apply_plan = entries.map(e => e.item);
      try {
        const r = await api.post(`/api/v2/campaign-grid/${campaignId}/run-agent`, {
          agentType,
          apply_plan,
          ...(applyBatchId ? { apply_batch_id: applyBatchId } : {}),
          apply_tracking: entries.map(({ rec, item }) => ({
            recommendationId: String(rec._key).startsWith('db:') ? String(rec._key).slice(3) : null,
            type: item.type,
            name: item.name,
          })).filter(item => item.recommendationId),
          accountId, userId,
        });
        const run = r.data.run;
        await new Promise((resolve) => {
          pollRun(run.id, (done) => {
            if (done.status !== 'error') {
              const ae = done.result?.applied_entities || {};
              // Flatten all result buckets (only the campaign's platform bucket
              // is populated). g-ads: applied_entities.{search_ad_groups,
              // pmax_search_themes, demandgen_targeting}; LinkedIn:
              // applied_campaigns; Facebook: applied_ad_sets. Match each back to
              // its card by name (unique for search ad groups / linkedin campaigns
              // / fb ad sets), else FIFO within type.
              const created = [
                ...(ae.search_ad_groups || []),
                ...(ae.pmax_search_themes || []),
                ...(ae.demandgen_targeting || []),
                ...(done.result?.applied_campaigns || []),
                ...(done.result?.applied_ad_sets || []),
              ];
              const pending = [...entries];
              for (const c of created) {
                const cType = c.type || 'search_ad_group';
                // Search ad groups, LinkedIn campaigns, and FB ad sets have unique,
                // user-visible names → match by name; PMax/DemandGen share an
                // entity name → FIFO.
                let idx = (cType === 'search_ad_group' || cType === 'linkedin_campaign' || cType === 'fb_ad_set')
                  ? pending.findIndex(e => e.item.type === cType && e.item.name === c.name)
                  : -1;
                if (idx < 0) idx = pending.findIndex(e => e.item.type === cType);
                const entry = idx >= 0 ? pending.splice(idx, 1)[0] : null;
                const succeeded = c.success !== false;
                if (succeeded) {
                  ok += 1;
                  if (entry) {
                    appliedKeys.add(entry.rec._key);
                    runIdByKey.set(entry.rec._key, run.id);
                    // Capture the created entity's resource/id for the card link.
                    // Search & DemandGen create a new paused ad group and return
                    // `ad_group_resource_name`. PMax now ALWAYS creates a new asset
                    // group and returns `new_asset_group_id` — older PMax responses
                    // had no created group or used `overflow_asset_group`, so accept
                    // both shapes and fall through to null when nothing was created.
                    const appliedResource = c.ad_group_resource_name
                      || c.new_asset_group_id
                      || c.new_asset_group?.new_asset_group_id
                      || c.overflow_asset_group?.new_asset_group_id
                      || null;
                    if (appliedResource) appliedResourceByKey.set(entry.rec._key, String(appliedResource));
                  }
                } else {
                  failed += 1;
                }
                logEntries.push({
                  ok: succeeded,
                  action: applyActionLabel(cType),
                  recName: entry?.rec?.name || c.name || '(unnamed)',
                  campaignName: entry?.rec?._campaignName || '',
                  reason: succeeded ? null : shortenError(c.error),
                  ts: Date.now(),
                });
              }
            } else {
              failed += apply_plan.length;
              const reason = shortenError(done.error || done.result?.error);
              for (const e of entries) logEntries.push({
                ok: false, action: applyActionLabel(e.item.type),
                recName: e.rec.name || e.item.name || '(unnamed)',
                campaignName: e.rec._campaignName || '', reason, ts: Date.now(),
              });
            }
            resolve();
          });
        });
      } catch (e) {
        failed += apply_plan.length;
        const reason = shortenError(e?.response?.data?.error || e?.message);
        for (const en of entries) logEntries.push({
          ok: false, action: applyActionLabel(en.item.type),
          recName: en.rec.name || en.item.name || '(unnamed)',
          campaignName: en.rec._campaignName || '', reason, ts: Date.now(),
        });
      }
    }));

    setApplying(false);
    if (logEntries.length) setApplyLog(prev => [...prev, ...logEntries]);
    if (appliedKeys.size) {
      setRecs(prev => prev.map(r => appliedKeys.has(r._key)
        ? { ...r, applied: true, selected: false, _appliedResource: appliedResourceByKey.get(r._key) || r._appliedResource || null }
        : r));
      // Persist the applied state so the locked cards survive a reload. Match by
      // persisted row id when the card came from the DB (exact — names collide
      // across PMax / DemandGen recs), else by campaign + name.
      const appliedItems = [];
      for (const group of byCampaign.values()) {
        for (const e of group.entries) {
          if (!appliedKeys.has(e.rec._key)) continue;
          const dbId = String(e.rec._key).startsWith('db:') ? String(e.rec._key).slice(3) : null;
          // Include the category (source) so the by-(campaign,name) fallback
          // can't cross-mark a same-named rec in the OTHER category — possible
          // now that the delete is category-scoped (a Moments and a
          // Recommendations rec can share a name in one campaign).
          appliedItems.push({
            id: dbId,
            campaignId: e.rec._campaignId,
            name: e.item.name,
            source: e.rec._source || null,
            runId: runIdByKey.get(e.rec._key) || null,
            appliedResourceName: appliedResourceByKey.get(e.rec._key) || null,
          });
        }
      }
      if (appliedItems.length) {
        try {
          await api.post(`/api/v2/campaign-grid/recommendations/applied`, { items: appliedItems, accountId, userId });
          await loadImpacts();
        } catch (_) { /* best-effort; UI already reflects applied */ }
      }
    }
    // Red when nothing applied (pure failure); green otherwise, still noting any
    // partial failures. The per-rec reasons show in the apply-log panel.
    if (ok === 0 && failed > 0) {
      setToast({ msg: `${failed} recommendation${failed === 1 ? '' : 's'} failed — see details below`, err: true });
    } else {
      setToast({ msg: `Applied ${ok} recommendation${ok === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}` });
    }
    loadCampaigns();
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  const filterCampaign = filterId ? campaigns.find(c => c.id === filterId) : null;
  const campaignsById = useMemo(() => {
    const m = new Map();
    for (const c of campaigns) m.set(c.id, c);
    return m;
  }, [campaigns]);
  const visibleImpacts = useMemo(
    () => filterId ? impacts.filter(i => i.campaign_id === filterId) : impacts,
    [impacts, filterId],
  );

  return (
    <div className="pg-root">
      <div className="pg-topbar">
        <div className="pg-bc">
          <button type="button" className="pg-bc-link" onClick={() => navigate('/campaigns')}>Campaigns</button> / <strong>Triggers</strong>
        </div>
        <span className="pg-badge">BETA</span>
        <div className="pg-tb-right">
          <div className="pg-tb-action">
            <button className="pg-btn pg-btn-secondary" type="button" onClick={() => setContextOpen(true)}>
              Context
            </button>
            <span className="pg-refreshed-mini">Advertiser or campaign</span>
          </div>
          {userIsSuperAdmin && (
            <div className="pg-tb-action">
              <button
                className="pg-btn pg-btn-secondary"
                onClick={() => setMomentsPickerOpen(true)}
                disabled={runningTriggers || running || loading}
                title="Super Admin: run this account's Moments now (instead of waiting for the weekly cron) — pick the whole account or just a few campaigns"
              >
                {runningTriggers ? 'Running…' : '⚡ Run Moments'}
              </button>
              <span className="pg-refreshed-mini">
                {runningTriggers
                  ? 'Running…'
                  : (triggersLastRunAt ? triggersRefreshedLabel : 'Super Admin · this account')}
              </span>
            </div>
          )}
          <div className="pg-tb-action">
            <button
              className="pg-btn pg-btn-ai"
              onClick={() => runAll()}
              disabled={running || runningTriggers || campaigns.length === 0}
            >
              {running ? 'Running…' : '✦ Run Recommendations'}
            </button>
            <span className="pg-refreshed-mini">
              {running ? 'Refreshing…' : (recsRefreshedLabel || 'Not refreshed yet')}
            </span>
          </div>
          {/* NO picker here: Triggers reads the campaign-grid dataset as
              persisted. This label reports the window that dataset covers —
              change it from the Campaign Grid page. */}
          <div className="pg-tb-action pg-time-filter" title="The reporting window the cached campaign-grid numbers cover — the window the Campaign Grid's last pull ran with. Change it on the Campaign Grid page.">
            <span className="pg-refreshed-mini pg-data-window">
              {dataWindow ? dataWindow.label : 'Data: not pulled yet'}
            </span>
          </div>
          <div className="pg-tb-action">
            <span className="pg-refreshed-mini">
              {metricsRefreshedLabel || 'Not refreshed yet'}
            </span>
          </div>
        </div>
      </div>

      {/* ── TOP HALF — Triggers Agent ─────────────────────────────────── */}
      <div className="pg-agent">
        <div className="pg-agent-head">
          <div>
            <div className="pg-agent-title">⚡ Triggers Agent</div>
            <div className="pg-agent-sub">
              {filterCampaign
                ? <>Moments for <strong>{filterCampaign.name}</strong> · <button className="pg-link" onClick={() => setFilterId(null)}>show all</button></>
                : 'Refreshed weekly across all active campaigns. Edit, select, then apply.'}
            </div>
          </div>
        </div>

        <div className="pg-recs-wrap">
          <h2 className="pg-moments-heading">
            Moments <span className="pg-moments-count">{activeRecs.length}</span>
          </h2>

          <div className="pg-recs-bar">
            <span className="pg-recs-count">
              {`${activeRecs.length} moment${activeRecs.length === 1 ? '' : 's'}${filterId ? ' (filtered)' : ''} · ${selectedCount} selected${appliedCount ? ` · ${appliedCount} applied` : ''}`}
            </span>
            {selectableCount > 0 && (
              <button className="pg-link" onClick={toggleSelectAll}>
                {selectedCount > 0 ? 'Deselect all' : 'Select all'}
              </button>
            )}
            {!applyButtonDisabled && (
              <button
                className="pg-btn pg-btn-apply"
                onClick={applySelected}
                disabled={applying || selectedCount === 0}
              >
                {applying ? 'Applying…' : `Apply ${selectedCount} selected →`}
              </button>
            )}
          </div>

          {applyLog.length > 0 && (
            <div className="pg-apply-log">
              <div className="pg-apply-log-head">
                <span className="pg-apply-log-title">
                  <strong>{applyLog.filter(e => e.ok).length}</strong> applied
                  {applyLog.some(e => !e.ok) && <span className="pg-apply-err"> · {applyLog.filter(e => !e.ok).length} failed</span>}
                  {applyLog.some(e => e.ok && e.action.startsWith('New ad group')) && <span className="pg-apply-note"> — new ad groups are created paused</span>}
                </span>
                <button className="pg-apply-log-x" onClick={() => setApplyLog([])} title="Dismiss" aria-label="Dismiss confirmations">✕</button>
              </div>
              <ul className="pg-apply-log-list">
                {applyLog.map((e, i) => (
                  <li key={i} className={`pg-apply-log-item ${e.ok ? 'ok' : 'fail'}`}>
                    <span className="pg-apply-log-ic">{e.ok ? '✓' : '⚠'}</span>
                    <div className="pg-apply-log-b">
                      <div className="pg-apply-log-action">{e.action}{e.ok ? '' : ' — failed'}</div>
                      <div className="pg-apply-log-meta">
                        <strong>{e.recName}</strong>{e.campaignName ? ` · ${e.campaignName}` : ''} · {fmtApplyTime(e.ts)}
                      </div>
                      {!e.ok && e.reason && <div className="pg-apply-log-reason">{e.reason}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pg-recs">
            {runningTriggers && (
              <div className="pg-recs-loading">
                <span className="pg-spinner" /> Still running the agent{triggersElapsedLabel ? ` (${triggersElapsedLabel} elapsed)` : ''} — moments load when the run finishes.
              </div>
            )}
            {running && (
              <div className="pg-recs-loading">
                <span className="pg-spinner" /> Analyzing {analyzingCount} campaign{analyzingCount === 1 ? '' : 's'}…
              </div>
            )}
            {recsLoading && !runningTriggers && !running && (
              <div className="pg-recs-loading"><span className="pg-spinner" /> Loading moments…</div>
            )}
            {momentsError && <div className="pg-recs-loading pg-recs-error">⚠ {momentsError}</div>}
            {recsError && <div className="pg-recs-loading pg-recs-error">⚠ {recsError}</div>}
            {!runningTriggers && !running && !recsLoading && !momentsError && !recsError && activeRecs.length === 0 && (
              <div className="pg-recs-loading">No moments{filterId ? ' for this campaign' : ''} yet.</div>
            )}
            {activeRecs.map(rec => (
              <RecommendationCard
                key={rec._key}
                rec={rec}
                appliedLink={buildAdsLink(rec, campaignsById.get(rec._campaignId))}
                currencyCode={campaignsById.get(rec._campaignId)?.currency_code}
                onChange={(patch) => updateRec(rec._key, patch)}
                onToggle={() => toggleRec(rec._key)}
                onReview={() => reviewMoment(rec)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── BOTTOM HALF — mini campaign grid (flat, no expand/collapse) ── */}
      <div className="pg-grid">
        <div className="pg-grid-head">
          <div className="pg-grid-switch" role="tablist" aria-label="Performance grid views">
            <button type="button" role="tab" aria-selected={gridView === 'campaigns'}
              className={gridView === 'campaigns' ? 'active' : ''} onClick={() => setGridView('campaigns')}>
              Campaigns <span>{filtered.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={gridView === 'impacts'}
              className={gridView === 'impacts' ? 'active' : ''} onClick={() => setGridView('impacts')}>
              Change impact <span>{visibleImpacts.length}</span>
            </button>
          </div>
          {gridView === 'campaigns' ? (
            <>
              <input
                className="pg-grid-search"
                placeholder="Search campaigns"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <span className="pg-grid-hint">Click a campaign to filter recommendations</span>
            </>
          ) : (
            <span className="pg-grid-hint">Observed campaign performance since Apply change · refreshed with grid metrics</span>
          )}
        </div>
        <div className="pg-grid-scroll">
          {gridView === 'campaigns' && loading && (
            <div className="pg-loading pg-loading-busy">
              <span className="pg-spinner" /> Loading campaigns…
            </div>
          )}
          {gridView === 'campaigns' && error && <div className="pg-loading pg-err">{error}</div>}
          {gridView === 'campaigns' && !loading && filtered.length === 0 && <div className="pg-empty">No campaigns found.</div>}
          {gridView === 'campaigns' && filtered.length > 0 && (
            <table className="pg-tbl">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Format</th>
                  <th>Folder</th>
                  <th>Status</th>
                  {/* Pacing hidden: pacing_pct is spend / (budget * days), and
                      the platform budget is a DAILY figure for most campaigns,
                      so it read as 1865% against a $30 budget. Same removal as
                      the Campaign Performance Grid. */}
                  {/* 'Spend / Budget' removed alongside Pacing: its inline
                      percentage was spend/budget off the same DAILY budget
                      (680% for $203.88 against $30.00) and the "/ budget" half
                      presented a daily figure as the campaign total. */}
                  {/* Metric columns match the Campaign Performance Grid exactly:
                      spend, impressions, clicks, conversions, KPI (actual only).
                      Spend is the plain delivered cost — no budget half and no
                      attainment percentage, which is what made the old
                      'Spend / Budget' column misleading. */}
                  <th className="pg-num">Spend</th>
                  <th className="pg-num">Impressions</th>
                  <th className="pg-num">Clicks</th>
                  <th className="pg-num">Conversions</th>
                  <th>KPI</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  // Actual only — the KPI target came from the launch plan, not
                  // the platform, so the "vs target" comparison (and its
                  // met/miss pill) is gone along with the target column.
                  const kpiActual = c.kpi_actual;
                  return (
                    <tr
                      key={c.id}
                      className={`pg-row ${filterId === c.id ? 'selected' : ''}`}
                      onClick={() => setFilterId(id => id === c.id ? null : c.id)}
                    >
                      <td className="pg-row-name">{c.name}</td>
                      <td><span className="pg-pill pg-pill-blue">{destinationFormat(c.destination)}</span></td>
                      <td className="pg-muted">{c.folder_name || '—'}</td>
                      <td><StatusPill status={campaignDisplayStatus(c)} /></td>
                      <td className="pg-num">{cur(c.spend, c.currency_code)}</td>
                      <td className="pg-num">{num(c.impressions)}</td>
                      <td className="pg-num">{num(c.clicks)}</td>
                      <td className="pg-num">{numFlex(c.conversions)}</td>
                      <td>
                        {c.kpi_label ? (
                          <span>
                            <strong>{c.kpi_label}</strong>:{' '}
                            {kpiActual != null ? (Number(kpiActual) * 100).toFixed(2) + '%' : '—'}
                          </span>
                        ) : <span className="pg-dash">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {gridView === 'impacts' && impactsLoading && (
            <div className="pg-loading pg-loading-busy"><span className="pg-spinner" /> Loading change history…</div>
          )}
          {gridView === 'impacts' && !impactsLoading && visibleImpacts.length === 0 && (
            <div className="pg-empty">No applied changes have been tracked yet.</div>
          )}
          {gridView === 'impacts' && !impactsLoading && visibleImpacts.length > 0 && (
            <table className="pg-tbl pg-impact-table">
              <thead><tr>
                <th>Applied change</th><th>Campaign</th><th>Status</th>
                <th>CTR before → after</th><th>CVR before → after</th><th>Measurement</th>
              </tr></thead>
              <tbody>{visibleImpacts.map(impact => (
                <tr key={impact.id}>
                  <td>
                    <strong>{impact.recommendation_name || REC_TYPE_LABEL[impact.recommendation_type] || 'Recommendation'}</strong>
                    <small>{REC_TYPE_LABEL[impact.recommendation_type] || impact.recommendation_type} · {fmtImpactDate(impact.applied_at)}</small>
                  </td>
                  <td>{impact.campaign_name}</td>
                  <td>
                    <span className={`pg-impact-status ${impact.status}`}>{IMPACT_STATUS[impact.status] || impact.status}</span>
                    {Number(impact.overlapping_changes) > 0 && <small className="pg-impact-warning">Overlaps another apply</small>}
                  </td>
                  <td><ImpactRate before={impact.baseline_ctr} after={impact.observed_ctr} delta={impact.ctr_delta} /></td>
                  <td><ImpactRate before={impact.baseline_conversion_rate} after={impact.observed_conversion_rate} delta={impact.conversion_rate_delta} /></td>
                  <td className="pg-impact-window">
                    <small>Before: {fmtWindow(impact.baseline_window)}</small>
                    <small>After: {impact.observed_fetched_at ? fmtWindow(impact.observed_window) : 'Waiting for next metrics refresh'}</small>
                    {impact.observed_impressions != null && <small>{num(impact.observed_impressions)} impressions · {num(impact.observed_clicks)} clicks</small>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>

      {momentsPickerOpen && (
        <RunMomentsModal
          campaigns={campaigns}
          onCancel={() => setMomentsPickerOpen(false)}
          onRun={(ids) => { setMomentsPickerOpen(false); runTriggers(ids); }}
        />
      )}

      {toast && <div className={`pg-toast ${toast.err ? 'err' : ''}`}>{toast.msg}</div>}
      {contextOpen && (
        <ContextEnginePanel
          accountId={accountId}
          userId={userId}
          campaigns={campaigns}
          defaultCampaignId={filterId}
          onClose={() => setContextOpen(false)}
        />
      )}
    </div>
  );
}

export default function PerfGridRoute() {
  const [params] = useSearchParams();
  const { getAccountIdToUse } = useAccount();
  const planId = params.get('momentPlan');
  const campaignId = params.get('campaign');
  const pmaxMoment = params.get('pmaxMoment');
  if (pmaxMoment && campaignId) return <PmaxMomentReview key={`${getAccountIdToUse()}:${campaignId}:${pmaxMoment}`} campaignId={campaignId} recommendationId={pmaxMoment} />;
  const searchMoment = params.get('searchMoment');
  if (searchMoment && campaignId) return <SearchMomentReview key={`${getAccountIdToUse()}:${campaignId}:${searchMoment}`} campaignId={campaignId} recommendationId={searchMoment} />;
  return planId && campaignId ? <MomentReview key={`${getAccountIdToUse()}:${campaignId}:${planId}`} campaignId={campaignId} planId={planId} /> : <PerfGridPage />;
}
